import { useEffect, useRef, useState } from "react";

/**
 * Computes pixel-accurate overlay position for a bbox drawn over an
 * object-contain image. Accounts for letterbox/pillarbox gutters and
 * recomputes on resize.
 *
 * Usage:
 *   const { imgRef, overlayStyle } = useBboxOverlay(ph.bbox);
 *   <img ref={imgRef} ... />
 *   {overlayStyle && <div style={overlayStyle} className="pointer-events-none absolute ..." />}
 */
export function useBboxOverlay(bboxJson: string | null | undefined) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties | null>(null);

  useEffect(() => {
    const img = imgRef.current;
    if (!img || !bboxJson) {
      setOverlayStyle(null);
      return;
    }

    let parsed: [number, number, number, number];
    try {
      parsed = JSON.parse(bboxJson) as [number, number, number, number];
    } catch {
      setOverlayStyle(null);
      return;
    }

    function compute() {
      if (!img || !img.naturalWidth) return;
      const [bx, by, bw, bh] = parsed;
      const containerW = img.clientWidth;
      const containerH = img.clientHeight;
      if (!containerW || !containerH) return;

      const naturalAR = img.naturalWidth / img.naturalHeight;
      const containerAR = containerW / containerH;

      let renderedW: number, renderedH: number, offsetX: number, offsetY: number;
      if (naturalAR > containerAR) {
        // letterboxed — gutters top + bottom
        renderedW = containerW;
        renderedH = containerW / naturalAR;
        offsetX = 0;
        offsetY = (containerH - renderedH) / 2;
      } else {
        // pillarboxed — gutters left + right
        renderedH = containerH;
        renderedW = containerH * naturalAR;
        offsetX = (containerW - renderedW) / 2;
        offsetY = 0;
      }

      setOverlayStyle({
        position: "absolute",
        left:   offsetX + bx * renderedW,
        top:    offsetY + by * renderedH,
        width:  bw * renderedW,
        height: bh * renderedH,
      });
    }

    img.addEventListener("load", compute);
    if (img.complete && img.naturalWidth) compute();

    const ro = new ResizeObserver(compute);
    ro.observe(img);

    return () => {
      img.removeEventListener("load", compute);
      ro.disconnect();
    };
  }, [bboxJson]);

  return { imgRef, overlayStyle };
}
